import React from "react";

/**
 * Skeleton primitives — the ONLY loading placeholders in the product.
 *
 * House rules baked in here so no screen has to re-decide them:
 *   • One neutral grey token (`bg-skeleton`). Never brand or directional
 *     colour — teal / green / red / gold all carry meaning in this product and
 *     must not appear on a screen that has no data yet.
 *   • Motion is a slow opacity breathe, never a lateral shimmer (fast sideways
 *     motion reads as market movement in a trading UI). Reduced-motion kills it.
 *   • Every shape is `aria-hidden` and not focusable — a screen reader gets the
 *     region's `aria-busy` instead of a pile of meaningless boxes.
 *   • NEVER render a placeholder value ("$0.00", "0.00%", "--"). Any of those
 *     can be misread as a real balance, a flat position, or an empty record.
 *     Grey blocks only.
 *
 * Compose pages from these. Do not build a bespoke skeleton system per screen.
 */

/** Base block. Everything below is a thin wrapper over this. */
export const SkeletonBlock = ({
  className = "",
  width,
  height,
  rounded = "rounded",
  animate = true,
  style,
}) => (
  <div
    aria-hidden="true"
    className={`bg-skeleton ${rounded} ${
      animate ? "animate-skeleton-pulse" : ""
    } ${className}`}
    style={{ width, height, ...style }}
  />
);

/** 1. Avatar circle. `size` is a px number or any CSS length. */
export const SkeletonAvatar = ({ size = 48, className = "" }) => (
  <SkeletonBlock
    rounded="rounded-full"
    width={size}
    height={size}
    className={`shrink-0 ${className}`}
  />
);

/**
 * 2. Text line. `lines > 1` renders a paragraph; the last line is shortened so
 * the block reads as prose rather than a filled rectangle.
 */
export const SkeletonText = ({
  lines = 1,
  width = "100%",
  lastLineWidth = "60%",
  height = 12,
  gap = 8,
  className = "",
}) => {
  if (lines === 1) {
    return (
      <SkeletonBlock width={width} height={height} className={className} />
    );
  }
  return (
    <div
      aria-hidden="true"
      className={`flex flex-col ${className}`}
      style={{ gap }}
    >
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBlock
          key={i}
          height={height}
          width={i === lines - 1 ? lastLineWidth : width}
        />
      ))}
    </div>
  );
};

/**
 * 3. Stat block — a label line over a value line. This is the shape that
 * replaces every numeric readout while it loads (balance, P&L, rank, count).
 */
export const SkeletonStat = ({
  labelWidth = 56,
  valueWidth = 84,
  align = "left",
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`flex flex-col gap-2 ${
      align === "center"
        ? "items-center"
        : align === "right"
        ? "items-end"
        : "items-start"
    } ${className}`}
  >
    <SkeletonBlock height={10} width={labelWidth} />
    <SkeletonBlock height={16} width={valueWidth} />
  </div>
);

/**
 * 4. Table / list row. `avatar` adds a leading circle, `columns` sets how many
 * trailing value columns sit on the right.
 */
export const SkeletonRow = ({
  avatar = false,
  avatarSize = 40,
  columns = 1,
  columnWidths,
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`flex items-center gap-3 ${className}`}
  >
    {avatar && <SkeletonAvatar size={avatarSize} />}
    <div className="flex-1 flex flex-col gap-2 min-w-0">
      <SkeletonBlock height={12} width="45%" />
      <SkeletonBlock height={10} width="28%" />
    </div>
    {Array.from({ length: columns }).map((_, i) => (
      <div key={i} className="flex flex-col gap-2 items-end shrink-0">
        <SkeletonBlock
          height={12}
          width={(columnWidths && columnWidths[i]) || 64}
        />
        <SkeletonBlock
          height={10}
          width={((columnWidths && columnWidths[i]) || 64) * 0.6}
        />
      </div>
    ))}
  </div>
);

/**
 * 5. Card body. The card CHROME (border, radius, padding, heading) should be
 * rendered by the real screen so it appears instantly; this fills only the
 * data region inside it.
 */
export const SkeletonCardBody = ({ lines = 3, className = "" }) => (
  <div aria-hidden="true" className={`flex flex-col gap-3 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBlock
        key={i}
        height={14}
        width={i === lines - 1 ? "55%" : i % 2 ? "78%" : "92%"}
      />
    ))}
  </div>
);

/**
 * 6. Chart rectangle. Always pass the chart's FINAL height so real data
 * arriving does not shove the page.
 */
export const SkeletonChart = ({ height = 240, className = "" }) => (
  <SkeletonBlock
    height={height}
    width="100%"
    rounded="rounded-lg"
    className={className}
  />
);

/**
 * Wrapper for a data region while its skeleton is on screen. Marks the region
 * busy for assistive tech and keeps the skeleton out of the tab order.
 *
 *   <SkeletonRegion busy={isLoading} label="Performance">
 *     {isLoading ? <SkeletonChart height={240} /> : <RealChart />}
 *   </SkeletonRegion>
 */
export const SkeletonRegion = ({
  busy,
  label,
  as: Tag = "div",
  className = "",
  children,
  ...rest
}) => (
  <Tag
    aria-busy={busy ? "true" : undefined}
    aria-label={busy && label ? `${label} loading` : undefined}
    className={className}
    {...rest}
  >
    {children}
  </Tag>
);

/** Cross-fade shell for real content arriving in place of a skeleton. */
export const SkeletonReveal = ({ className = "", children }) => (
  <div className={`skeleton-fade-in ${className}`}>{children}</div>
);

export default SkeletonBlock;
